import {captureException} from '@famly/vet_services_sentry';

/**
 * Remembers that this tab has already spent its one reload. Session-scoped on purpose: the reload
 * it guards only helps within the tab that failed.
 */
const RELOAD_ATTEMPTED_KEY = 'famly.appChunkReloadAttempted';

const isChunkLoadError = (error: unknown): boolean => error instanceof Error && error.name === 'ChunkLoadError';

/**
 * Both storage reads and writes throw when storage is unavailable, as in Safari's private browsing.
 * A tab that cannot remember an attempt must not reload, or it would do so on every failure.
 */
const hasAttemptedReload = (): boolean => {
    try {
        return window.sessionStorage.getItem(RELOAD_ATTEMPTED_KEY) !== null;
    } catch {
        return true;
    }
};

const markReloadAttempted = (): boolean => {
    try {
        window.sessionStorage.setItem(RELOAD_ATTEMPTED_KEY, 'true');

        return true;
    } catch {
        return false;
    }
};

/**
 * Decides how to handle the app's entry chunk failing to load, and reports the failures a reload
 * cannot fix (APP-PRODUCTION-ND2).
 *
 * Nothing renders when that chunk never arrives, so the user is left on a blank page. The bundler
 * runtime already retries the download a few times; what reaches here is a chunk the browser
 * cannot get at all — typically an `index.html` cached from an earlier deploy, pointing at hashed
 * assets that no longer exist. Reloading fetches the current document and with it the current
 * asset names.
 *
 * Only the first failure in a tab is worth reloading for: if the freshly fetched document fails
 * the same way, a stale document was not the cause and reloading would strand the user in a loop.
 *
 * @returns `true` when the caller should reload the page, having claimed this tab's one attempt
 */
export const shouldReloadAfterAppChunkLoadError = (error: unknown): boolean => {
    if (isChunkLoadError(error) && !hasAttemptedReload() && markReloadAttempted()) {
        return true;
    }

    captureException(error);

    return false;
};
